import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { useCompanyById } from '../../hooks/api/useCompanies'
import { useCompanyUsers } from '../../hooks/api/useUsers'
import { useDevices } from '../../hooks/api/useDevices'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { LoadingState } from '../../components/ui/LoadingState'
import { ErrorState } from '../../components/ui/ErrorState'
import { formatDeviceForDisplay } from '../../utils/device-utils'
import { DevicesTableWithBulkActions } from '../../components/admin/DevicesTableWithBulkActions'
import { useQueryClient } from '@tanstack/react-query'

export const AdminOrganizationDetailPage = () => {
  const navigate = useNavigate()
  const { companyId } = useParams<{ companyId: string }>()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { data: userRecord, isLoading: userLoading, error: userError } = useUserCheck(user?.id || '')
  const { data: company, isLoading: companyLoading, error: companyError } = useCompanyById(companyId)
  const { data: members = [], isLoading: membersLoading } = useCompanyUsers(companyId)
  const { data: devices = [], isLoading: devicesLoading } = useDevices(companyId || '')

  const manager = useMemo(
    () => members.find(member => member.role === 'manager'),
    [members]
  )
  const managerStatus = manager?.is_active ? 'Active' : 'Inactive'
  const managerStatusClasses = manager?.is_active
    ? 'bg-green-100 text-green-700'
    : 'bg-gray-100 text-gray-600'

  const formattedDevices = useMemo(() => devices.map(formatDeviceForDisplay), [devices])

  if (userLoading || companyLoading || membersLoading || devicesLoading) {
    return <LoadingState message="Loading organization..." />
  }

  if (userError || companyError || !userRecord || !company) {
    return <ErrorState title="Organization not found" description="Please return to the organizations list." />
  }

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-6">
        <button
          onClick={() => navigate('/admin/organizations')}
          className="inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{company.name}</h1>
            <p className="text-sm text-gray-500">Organization overview, devices, and recent activity.</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
              Billing History
            </button>
            <button className="rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
              Edit Organization
            </button>
            <button className="rounded-full border border-red-200 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
              Delete
            </button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Organization Information</h2>
            <div className="mt-4 flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                {company.name.slice(0, 2).toUpperCase()}
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="font-medium text-gray-900">{company.name}</div>
                <div>{manager?.email || 'No contact email on file'}</div>
                <div className="capitalize">{company.subscription_tier || 'Essentials'}</div>
                <div className="text-xs text-gray-400">Slug: {company.slug}</div>
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Manager</h2>
            {manager ? (
              <div className="mt-4 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                  {manager.full_name ? manager.full_name.slice(0, 2).toUpperCase() : 'MG'}
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="font-medium text-gray-900">{manager.full_name || manager.email}</div>
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${managerStatusClasses}`}>
                    {managerStatus}
                  </span>
                  <div>{manager.email}</div>
                </div>
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-500">
                <p>No organization manager has been assigned.</p>
                <button className="mt-3 rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">
                  Send Welcome Notification
                </button>
              </div>
            )}
          </section>
        </div>

        <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Devices</h2>
          </div>

          <DevicesTableWithBulkActions
            devices={formattedDevices}
            companyId={companyId || ''}
            onDevicesUpdated={() => {
              // Invalidate devices cache to refetch
              queryClient.invalidateQueries({ queryKey: ['devices', companyId] })
            }}
          />
        </section>

        <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Recent Activity</h2>
          <div className="mt-6 rounded-2xl border border-dashed border-gray-200 py-12 text-center text-sm text-gray-500">
            Recent activity will appear here once events occur.
          </div>
        </section>
      </div>
    </DashboardLayout>
  )
}
